// Web Audio API recording service for microphone input

export interface RecordingState {
  isRecording: boolean
  isPaused: boolean
  duration: number
  error: string | null
}

export interface RecordingOptions {
  sampleRate?: number
  channelCount?: number
  bitDepth?: number
}

class RecordingService {
  private mediaRecorder: MediaRecorder | null = null
  private mediaStream: MediaStream | null = null
  private recordedChunks: Blob[] = []
  private recordingStartTime: number = 0
  private recordingDuration: number = 0
  private recordingTimer: number | null = null

  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      })

      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop())

      console.log('Microphone permission granted')
      return true
    } catch (error) {
      console.error('Microphone permission denied:', error)
      return false
    }
  }

  async startRecording(options: RecordingOptions = {}): Promise<void> {
    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: options.sampleRate || 44100
        }
      })

      // Create MediaRecorder
      const mimeType = this.getSupportedMimeType()
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType,
        audioBitsPerSecond: 128000
      })

      // Reset recorded chunks
      this.recordedChunks = []
      this.recordingStartTime = Date.now()
      this.recordingDuration = 0

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        console.log('Recording stopped')
      }

      this.mediaRecorder.onerror = (event) => {
        console.error('Recording error:', event)
        throw new Error('Recording failed')
      }

      // Start recording
      this.mediaRecorder.start(100) // Collect data every 100ms

      // Start duration timer
      this.recordingTimer = setInterval(() => {
        this.recordingDuration = Date.now() - this.recordingStartTime
      }, 100)

      console.log('Recording started')
    } catch (error) {
      console.error('Failed to start recording:', error)
      throw error
    }
  }

  stopRecording(): Blob | null {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return null
    }

    // Stop recording
    this.mediaRecorder.stop()

    // Clear timer
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
      this.recordingTimer = null
    }

    // Stop all tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }

    // Create blob from recorded chunks
    if (this.recordedChunks.length > 0) {
      const mimeType = this.getSupportedMimeType()
      const blob = new Blob(this.recordedChunks, { type: mimeType })
      console.log('Recording completed, blob size:', blob.size)
      return blob
    }

    return null
  }

  pauseRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause()
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer)
        this.recordingTimer = null
      }
      console.log('Recording paused')
    }
  }

  resumeRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume()
      this.recordingStartTime = Date.now() - this.recordingDuration
      this.recordingTimer = setInterval(() => {
        this.recordingDuration = Date.now() - this.recordingStartTime
      }, 100)
      console.log('Recording resumed')
    }
  }

  getRecordingDuration(): number {
    return this.recordingDuration
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording'
  }

  isPaused(): boolean {
    return this.mediaRecorder?.state === 'paused'
  }

  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return 'audio/webm' // Fallback
  }

  // Convert blob to ArrayBuffer for storage
  async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as ArrayBuffer)
      reader.onerror = reject
      reader.readAsArrayBuffer(blob)
    })
  }

  // Create audio URL from blob for playback
  createAudioURL(blob: Blob): string {
    return URL.createObjectURL(blob)
  }

  // Clean up resources
  cleanup(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
      this.recordingTimer = null
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }

    if (this.mediaRecorder) {
      this.mediaRecorder = null
    }

    this.recordedChunks = []
  }
}

// Export singleton instance
export const recordingService = new RecordingService()
